import type { KeyboardEvent } from "react";
import { useEffect, useId, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useItemSearch } from "../api/queries";
import type { ItemMatch } from "../api/types";
import { itemHref, nextIndex } from "../search/nav";

/** ⌘ on Apple hardware, Ctrl everywhere else. Read once, from the userAgent,
 *  because a wrong guess costs exactly one cosmetic label: the shortcut itself
 *  listens for both modifiers regardless, so nobody is locked out by it. That is
 *  the whole budget this question deserves. */
const HINT = /Mac|iPhone|iPad/.test(navigator.userAgent) ? "⌘K" : "Ctrl K";

/** Long enough that a touch-typist's burst of keys is one request instead of
 *  eight, short enough that the list is there by the time the eye arrives. A
 *  duration in TypeScript rather than a token because tokens.css governs CSS;
 *  auth.tsx's staleTime sets the same precedent. */
const DEBOUNCE_MS = 150;

/** Item search, in the header, because it is the one navigation that is not
 *  about a particular scan — you reach an item's months of history from
 *  anywhere, without first finding a scan that happens to contain it. */
export function ItemSearch() {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const [text, setText] = useState("");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);

  // The typed value and the queried value are separate state on purpose: the
  // input has to stay instant while the query behind it lags a beat.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(text.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text]);

  // A new query is a new list, and a highlight left pointing at position 4 of
  // the old one selects an unrelated item on the next Enter.
  useEffect(() => setHighlight(-1), [query]);

  useEffect(() => {
    function onKeyDown(event: globalThis.KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // pointerdown, not click: a click that lands on a table row below should close
  // the dropdown before the row reacts, not after it has already redrawn.
  useEffect(() => {
    function onPointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, []);

  // Any navigation closes it, including the browser's own back button, which is
  // how a dropdown otherwise survives onto a page it has nothing to do with.
  useEffect(() => setOpen(false), [pathname, search]);

  const results = useItemSearch(query);
  const rows = results.data ?? [];
  const showing = open && query !== "";

  function openMatch(match: ItemMatch) {
    navigate(itemHref(match.id, match.canonical_name));
    setText("");
    setQuery("");
    setOpen(false);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      setText("");
      setQuery("");
      setOpen(false);
      event.currentTarget.blur();
      return;
    }
    // Everything below drives a list, so none of it may run unless that list is
    // actually on screen. rows outlives the dropdown: useItemSearch keeps its
    // placeholderData across the key change onto the empty query, so a box
    // backspaced empty still holds the previous search's results. Ungated, Enter
    // there opened the first match of a query the reader had just deleted, and
    // the arrows swallowed the page's own scrolling while nothing was shown.
    if (!showing) return;

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      // Without this the page scrolls behind the open dropdown, so the row under
      // the cursor is no longer the row the cursor was aimed at.
      event.preventDefault();
      const direction = event.key === "ArrowDown" ? 1 : -1;
      setHighlight((index) => nextIndex(index, rows.length, direction));
      return;
    }
    if (event.key === "Enter") {
      // Nothing highlighted means take the first: typing a name and pressing
      // Enter is the fast path, and it should not need an ArrowDown first.
      const match = rows[highlight] ?? rows[0];
      if (match) {
        event.preventDefault();
        openMatch(match);
      }
    }
  }

  let list;
  if (results.isLoading) {
    list = <li role="presentation" className="dim">searching…</li>;
  } else if (results.isError) {
    list = <li role="presentation" className="dim">Search failed.</li>;
  } else if (rows.length === 0) {
    list = <li role="presentation" className="dim">No item matching “{query}”.</li>;
  } else {
    list = rows.map((match, index) => (
      <li
        key={match.id}
        id={`${listId}-option-${index}`}
        role="option"
        aria-selected={index === highlight}
        className={index === highlight ? "sel" : undefined}
        onMouseEnter={() => setHighlight(index)}
        onClick={() => openMatch(match)}
      >
        {match.canonical_name}
      </li>
    ));
  }

  return (
    <div className="item-search" ref={rootRef}>
      <input
        ref={inputRef}
        type="search"
        role="combobox"
        aria-label="Search items"
        aria-expanded={showing}
        // The listbox is only in the document while showing, and pointing at an
        // id no element carries is worse than not pointing at anything.
        aria-controls={showing ? listId : undefined}
        aria-activedescendant={highlight >= 0 ? `${listId}-option-${highlight}` : undefined}
        aria-autocomplete="list"
        autoComplete="off"
        placeholder="search items…"
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
      />
      {/* Decoration over an input that already carries its own label; announcing
          "command K" after "Search items" is one label too many. */}
      <span className="item-search-hint" aria-hidden="true">{HINT}</span>
      {showing ? (
        <ul className="item-search-list" id={listId} role="listbox" aria-label="Items">
          {/* The state lines are role="presentation" so a listbox never claims to
              hold an option a reader cannot choose. Their text still reads. */}
          {list}
        </ul>
      ) : null}
    </div>
  );
}
